import { HttpStatusCode } from 'axios';
import { errorStatus } from '@srm/core/errors';
import { BackgroundJobKind, IBackgroundJob, IBackgroundJobChangedEvent } from '@models/backgroundJobs/IBackgroundJob';
import { BackgroundJobsDisabledError, BackgroundJobsService } from '@services/BackgroundJobsService';
import { notificationHub } from '@/signalr/hubs/NotificationHub';
import {
    BackgroundJobsAction,
    backgroundJobsReducer,
    BackgroundJobsState,
    findJustFinished,
    initialBackgroundJobsState,
    selectActiveJobs,
} from './backgroundJobsReducer';

/** Страховочный опрос идущих задач, когда хаб не подключён и пуша нет. */
export const POLL_INTERVAL_MS = 10_000;

/**
 * Опрос при подключённом хабе: событие могло потеряться на переподключении, а пуш задач на сервере можно
 * выключить отдельно от хаба (`BackgroundJobs:Push:Enabled`).
 */
export const PUSH_POLL_INTERVAL_MS = 30_000;

/** Как часто окно отмечается «жду эту задачу» — с запасом внутри серверного окна отметки. */
export const WATCH_INTERVAL_MS = 15_000;

/**
 * Первое подключение хаба тоже объявляется переподключением — сразу после загрузки списка при входе, а возврат на
 * вкладку бывает и сразу после него. Пропущенным за такой промежуток событиям взяться неоткуда, а второй одинаковый
 * запрос не нужен. Настоящее переподключение (после разрыва) перечитывает список всегда: запрос, ушедший незадолго до
 * него, мог разминуться с событиями, разосланными без соединения.
 */
const LIST_RELOAD_GAP_MS = 1500;

/**
 * Паузы перед повтором списка после сбоя (не 404: сеть, выкладка). Дальше список перечитают переподключение хаба и
 * возврат на вкладку. Каждая — с разбросом ±`LIST_RETRY_JITTER` (`listRetryDelay`): вкладки, упавшие на одной выкладке,
 * не повторяют одной волной.
 */
export const LIST_RETRY_DELAYS_MS: readonly number[] = [5_000, 15_000, 60_000];

export const LIST_RETRY_JITTER = 0.5;

/**
 * Предел случайной паузы перед перечитыванием списка после переподключения хаба: после падения узла или выкладки
 * переподключаются сразу все вкладки, и без разброса их запросы пришли бы одной волной.
 */
export const RECONNECT_JITTER_MS = 10_000;

/**
 * Предел закончившихся задач в ответе списка (идущие сервер отдаёт все); идущую, которой в нём нет, клиент дочитывает
 * сам.
 */
export const LIST_LIMIT = 50;

/** Сколько после передачи файла браузеру кнопка ещё занята: повторный щелчок не начинает второе скачивание. */
export const NATIVE_DOWNLOAD_HOLD_MS = 1500;

/** Сколько вкладка помнит объявленный итог: уведомление о нём приходит в пределах минуты, дальше память не нужна. */
export const ANNOUNCED_MEMORY_MS = 10 * 60_000;

type JobListener = (job: IBackgroundJob | null) => void;
type FinishedListener = (job: IBackgroundJob) => void;

export interface IBackgroundJobsHub {
    readonly isConnected: boolean;
    onJobChanged(callback: (event: IBackgroundJobChangedEvent) => void): () => void;
    onReconnected(callback: () => void): () => void;
}

export interface IBackgroundJobsStoreDeps {
    service: Pick<
        typeof BackgroundJobsService,
        'list' | 'get' | 'cancel' | 'watch' | 'seen' | 'downloadFile' | 'downloadFileNative' | 'notifyFileGone'
    >;
    hub: IBackgroundJobsHub;
    now: () => number;
    /** Случайное число из [0, 1) — разброс пауз; по умолчанию `Math.random`. */
    random?: () => number;
}

export interface IObserveJobOptions {
    /** Отмечаться «жду эту задачу» (`POST watch`) из видимой вкладки, пока наблюдение не снято. По умолчанию — да. */
    watch?: boolean;
}

export interface IMarkShownOptions {
    /** Отметить итог увиденным на сервере (`POST seen`). По умолчанию — да; нет — если показавший отмечает сам. */
    seen?: boolean;
}

/**
 * Клиент фоновых задач: одно состояние на вкладку, общее для колокольчика и окон, которые ждут свою задачу.
 *
 * Состояние держится актуальным пушем `JobChanged` хаба уведомлений (по событию дочитывается `GET jobs/{id}`),
 * перечитывается целиком при запуске, переподключении хаба и возврате на вкладку; пока есть идущие задачи — ещё
 * и страховочным опросом. Выключенный на сервере механизм (404) клиент запоминает и больше не спрашивает.
 *
 * Окно, которое само показывает ход и итог задачи, регистрируется наблюдателем (`observe`): по её завершении шапка
 * не звонит и не показывает тост — итог перед глазами.
 */
export class BackgroundJobsStore {
    private state: BackgroundJobsState = initialBackgroundJobsState;
    private readonly listeners = new Set<() => void>();
    private readonly finishedListeners = new Set<FinishedListener>();
    private readonly jobListeners = new Map<string, Set<JobListener>>();
    private readonly observed = new Map<string, number>();
    /** Задачи, итог которых ещё объявят (сигнал отложен до возврата на вкладку): вытеснение их не трогает. */
    private readonly held = new Map<string, number>();
    /** Итоги, уже объявленные этой вкладке (тост, превью, окно): `jobId` → когда. */
    private readonly announced = new Map<string, number>();
    /**
     * Итоги, показанные окном или страницей этой вкладки: `jobId` → когда. Страница отчёта узнаёт об итоге своим
     * каналом и может показать его раньше, чем клиент узнает о задаче, — пометка ложится, когда задача придёт.
     */
    private readonly shownHere = new Map<string, number>();
    private readonly pendingJobs = new Map<string, Promise<IBackgroundJob | null | undefined>>();
    private readonly pendingDownloads = new Map<string, Promise<boolean>>();
    /** Задачи, чей файл сейчас скачивается: кнопки «Скачать» в окне, разделе и превью заняты. */
    private downloads: ReadonlySet<string> = new Set();
    private readonly downloadListeners = new Set<() => void>();
    private disposers: (() => void)[] = [];
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private listRetryTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    /** Хаб переподключился, пока вкладка была скрыта: список перечитается при возврате на неё. */
    private listStale = false;
    /** Сколько раз подряд список не прочитался. */
    private listFailures = 0;
    private lastListRequestAt = Number.NEGATIVE_INFINITY;
    /** Номер последнего запроса списка и последнего применённого ответа: ответы обгоняют друг друга. */
    private listRequestSeq = 0;
    private appliedListSeq = 0;
    /** Сколько открыто мест, где виден ход всех задач (раздел колокольчика). */
    private progressViewers = 0;
    /** Смена поколения (stop/start) отбрасывает ответы, ушедшие при прежнем пользователе. */
    private generation = 0;
    private started = false;

    constructor(private readonly deps: IBackgroundJobsStoreDeps) {}

    // ── Подписка React (useSyncExternalStore) ────────────────────────────────────────────────────────────────

    public readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    public readonly getSnapshot = (): BackgroundJobsState => this.state;

    public readonly subscribeDownloads = (listener: () => void): (() => void) => {
        this.downloadListeners.add(listener);
        return () => {
            this.downloadListeners.delete(listener);
        };
    };

    public readonly getDownloads = (): ReadonlySet<string> => this.downloads;

    // ── Жизненный цикл (шапка: вход и выход пользователя) ───────────────────────────────────────────────────

    public start(): void {
        if (this.started) return;
        this.started = true;
        this.generation += 1;

        const reloadList = () => {
            if (this.listStale || this.deps.now() - this.lastListRequestAt >= LIST_RELOAD_GAP_MS) this.refreshList();
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') reloadList();
        };
        let firstConnection = !this.deps.hub.isConnected;
        const onReconnected = () => {
            if (!firstConnection) {
                this.refreshListAfterReconnect();
                return;
            }
            firstConnection = false;
            reloadList();
        };
        document.addEventListener('visibilitychange', onVisibility);

        this.disposers = [
            this.deps.hub.onJobChanged((event) => this.onJobChanged(event)),
            this.deps.hub.onReconnected(onReconnected),
            () => document.removeEventListener('visibilitychange', onVisibility),
        ];

        this.refreshList();
    }

    public stop(): void {
        if (!this.started) return;
        this.started = false;
        this.generation += 1;
        this.disposers.forEach((dispose) => dispose());
        this.disposers = [];
        this.clearPoll();
        this.clearListRetry();
        this.clearReconnect();
        this.listFailures = 0;
        this.listStale = false;
        this.announced.clear();
        this.shownHere.clear();
        this.pendingJobs.clear();
        this.pendingDownloads.clear();
        this.lastListRequestAt = Number.NEGATIVE_INFINITY;
        this.dispatch({ type: 'reset' });
        if (this.downloads.size > 0) {
            this.downloads = new Set();
            this.downloadListeners.forEach((listener) => listener());
        }
    }

    // ── Чтение с сервера ────────────────────────────────────────────────────────────────────────────────────

    /** Перечитать список своих задач (идущие и не увиденные итоги). */
    public refreshList(): void {
        if (!this.started || this.state.enabled === false) return;
        const generation = this.generation;
        const requestedAt = this.deps.now();
        this.lastListRequestAt = requestedAt;
        this.listStale = false;
        this.listRequestSeq += 1;
        const seq = this.listRequestSeq;
        const isCurrent = () => {
            if (generation !== this.generation || seq < this.appliedListSeq) return false;
            this.appliedListSeq = seq;
            return true;
        };

        this.deps.service
            .list({ active: true, limit: LIST_LIMIT })
            .then((jobs) => {
                if (!isCurrent()) return;
                this.listFailures = 0;
                this.clearListRetry();
                const listed = new Set(jobs.map((job) => job.jobId));
                const unlisted = selectActiveJobs(this.state).filter((job) => !listed.has(job.jobId));
                this.dispatch({ type: 'listLoaded', jobs, requestedAt, now: this.deps.now() });
                unlisted.forEach((job) => void this.refreshJob(job.jobId));
            })
            .catch((error: unknown) => {
                if (!isCurrent()) return;
                if (error instanceof BackgroundJobsDisabledError) {
                    this.dispatch({ type: 'disabled' });
                    return;
                }
                this.scheduleListRetry();
                this.schedulePoll();
            });
    }

    /**
     * Дочитать одну задачу; `null` — её нет (уборка, чужая). Отказ сети — прежнее состояние. Одновременные запросы
     * одной задачи сливаются в один. При выключенном механизме спрашивает только о задаче, которую ждёт подписчик:
     * список мог ответить 404, а задача на сервере — идти.
     */
    public refreshJob(jobId: string): Promise<IBackgroundJob | null | undefined> {
        if (this.state.enabled === false && !this.jobListeners.has(jobId)) return Promise.resolve(undefined);
        const pending = this.pendingJobs.get(jobId);
        if (pending) return pending;
        const generation = this.generation;

        const request = this.deps.service
            .get(jobId)
            .then((job) => {
                if (generation !== this.generation) return undefined;
                if (job) this.dispatch({ type: 'jobLoaded', job, now: this.deps.now() });
                else this.forgetJob(jobId);
                return job;
            })
            .catch(() => undefined)
            .finally(() => {
                if (this.pendingJobs.get(jobId) === request) this.pendingJobs.delete(jobId);
            });
        this.pendingJobs.set(jobId, request);
        return request;
    }

    /**
     * Задача только что поставлена (ответ 202 фонового старта): показать её сразу, не дожидаясь пуша. Без полного
     * DTO — дочитать. Механизм, который клиент счёл выключенным, раз задача поставлена, включён: список — заново.
     */
    public track(job: IBackgroundJob | { jobId: string }): void {
        if (this.state.enabled === false) {
            this.dispatch({ type: 'recheck' });
            this.refreshList();
        }
        if ('status' in job && 'createdAt' in job) {
            this.dispatch({ type: 'jobLoaded', job, now: this.deps.now() });
        }
        void this.refreshJob(job.jobId);
    }

    public getJob(jobId: string): IBackgroundJob | undefined {
        return this.state.jobs[jobId]?.job;
    }

    // ── Подписки на задачи ──────────────────────────────────────────────────────────────────────────────────

    /**
     * Следить за одной задачей: обратный вызов получает каждое новое состояние (сразу — известное, если есть),
     * `null` — задачи больше нет. Незнакомую задачу клиент дочитывает сам.
     */
    public subscribeJob(jobId: string, listener: JobListener): () => void {
        let set = this.jobListeners.get(jobId);
        if (!set) {
            set = new Set();
            this.jobListeners.set(jobId, set);
        }
        set.add(listener);

        const known = this.state.jobs[jobId];
        if (known) listener(known.job);
        else void this.refreshJob(jobId);

        return () => {
            const current = this.jobListeners.get(jobId);
            current?.delete(listener);
            if (current?.size === 0) this.jobListeners.delete(jobId);
        };
    }

    /** Задача, которую клиент застал идущей, закончилась (любым итогом, включая отмену). */
    public onFinished(listener: FinishedListener): () => void {
        this.finishedListeners.add(listener);
        return () => {
            this.finishedListeners.delete(listener);
        };
    }

    // ── Наблюдатели: окна, которые сами показывают итог ─────────────────────────────────────────────────────

    /**
     * Окно показывает эту задачу само: шапка не звонит и не показывает тост о её завершении. По умолчанию ещё
     * и отмечается «жду» (`POST watch`), чтобы итог не ушёл в колокольчик, пока окно на него смотрит. Вызвать
     * возвращённую функцию — окно закрыто (или ушло в фон).
     */
    public observe(jobId: string, { watch = true }: IObserveJobOptions = {}): () => void {
        const release = retain(this.observed, jobId);
        const stopWatching = watch ? this.keepWatching(jobId) : undefined;
        let disposed = false;

        return () => {
            if (disposed) return;
            disposed = true;
            stopWatching?.();
            release();
        };
    }

    public isObserved(jobId: string): boolean {
        return this.observed.has(jobId);
    }

    /**
     * Итог задачи ещё будет объявлен (отложенный сигнал скрытой вкладки): держать её в состоянии, пока не вызвана
     * возвращённая функция, даже когда окно показа итога вышло.
     */
    public hold(jobId: string): () => void {
        return retain(this.held, jobId);
    }

    /** Открыто место, где виден ход всех задач (раздел колокольчика): пуш хода дочитывается и без подписчика. */
    public viewProgress(): () => void {
        this.progressViewers += 1;
        // Пока раздел был закрыт, пуши «только ход» не дочитывались: первому зрителю — свежий ход идущих. Скрытая
        // вкладка перечитает список, вернувшись.
        if (this.progressViewers === 1 && document.visibilityState !== 'hidden') {
            selectActiveJobs(this.state).forEach((job) => void this.refreshJob(job.jobId));
        }
        let disposed = false;
        return () => {
            if (disposed) return;
            disposed = true;
            this.progressViewers -= 1;
        };
    }

    /**
     * Итог задачи эта вкладка уже показала (тост шапки, превью уведомления, окно задачи): второй сигнал о том же не
     * нужен.
     */
    public markAnnounced(jobId: string): void {
        remember(this.announced, jobId, this.deps.now());
    }

    public wasAnnounced(jobId: string): boolean {
        const at = this.announced.get(jobId);
        return at !== undefined && this.deps.now() - at < ANNOUNCED_MEMORY_MS;
    }

    // ── Действия ────────────────────────────────────────────────────────────────────────────────────────────

    public async cancel(jobId: string): Promise<IBackgroundJob | null> {
        const job = await this.deps.service.cancel(jobId);
        if (job) this.dispatch({ type: 'jobLoaded', job, now: this.deps.now() });
        else this.forgetJob(jobId);
        return job;
    }

    /** Итог показан пользователю — уведомление о нём больше не нужно. */
    public markSeen(jobId: string): void {
        this.deps.service.seen(jobId).catch(() => {
            /* Отметка — довесок: без неё придёт лишнее уведомление, не больше. */
        });
    }

    /**
     * Итог задачи показало её окно (или страница) в этой вкладке: в разделе колокольчика он не повторяется, тост и
     * превью уведомления о нём не нужны, на сервере итог отмечается увиденным.
     */
    public markShownInWindow(jobId: string, { seen = true }: IMarkShownOptions = {}): void {
        this.markAnnounced(jobId);
        remember(this.shownHere, jobId, this.deps.now());
        this.dispatch({ type: 'shownInWindow', jobId });
        if (seen) this.markSeen(jobId);
    }

    /**
     * Пользователь скрыл строку итога в разделе: она уходит и не возвращается при перечитывании, итог увиден — и
     * уведомление о нём в ленту не нужно, и его превью у колокольчика.
     */
    public dismiss(jobId: string): void {
        this.markAnnounced(jobId);
        this.dispatch({ type: 'dismissed', jobId, now: this.deps.now() });
        this.markSeen(jobId);
    }

    /**
     * Скачать файл результата; скачанный файл — тоже увиденный итог. Отказ уже показан тостом — текстом вида задачи
     * (`kind`; без него — вида известной задачи). Пока идёт скачивание файла задачи, повторный вызов ждёт его же.
     */
    public downloadFile(jobId: string, kind?: BackgroundJobKind | null): Promise<boolean> {
        const pending = this.pendingDownloads.get(jobId);
        if (pending) return pending;
        const request = this.fetchFile(jobId, kind ?? this.getJob(jobId)?.kind).finally(() => {
            if (this.pendingDownloads.get(jobId) !== request) return;
            this.pendingDownloads.delete(jobId);
            this.setDownloading(jobId, false);
        });
        this.pendingDownloads.set(jobId, request);
        this.setDownloading(jobId, true);
        return request;
    }

    // ── Внутреннее ──────────────────────────────────────────────────────────────────────────────────────────

    /**
     * Свежее состояние задачи решает, как качать: файла уже нет — тост сразу (ответ 404 ссылке браузер не объяснит),
     * есть — браузером, потоком на диск. Узнать не удалось (сеть, нет токена для ссылки) — запросом, он сам покажет
     * отказ; его 404 — задача перечитывается, и кнопка «Скачать» уходит.
     */
    private async fetchFile(jobId: string, kind: BackgroundJobKind | undefined): Promise<boolean> {
        const generation = this.generation;
        const job = await this.refreshJob(jobId);
        const jobKind = kind ?? job?.kind;
        if (job === null || job?.hasResultFile === false) {
            this.deps.service.notifyFileGone(jobKind);
            return false;
        }
        if (job && this.deps.service.downloadFileNative(jobId)) {
            await new Promise((resolve) => setTimeout(resolve, NATIVE_DOWNLOAD_HOLD_MS));
        } else {
            try {
                await this.deps.service.downloadFile(jobId, jobKind);
            } catch (error) {
                if (errorStatus(error) === HttpStatusCode.NotFound) void this.refreshJob(jobId);
                return false;
            }
        }
        if (generation === this.generation) this.markSeen(jobId);
        return true;
    }

    private setDownloading(jobId: string, downloading: boolean): void {
        if (this.downloads.has(jobId) === downloading) return;
        const next = new Set(this.downloads);
        if (downloading) next.add(jobId);
        else next.delete(jobId);
        this.downloads = next;
        this.downloadListeners.forEach((listener) => listener());
    }

    /**
     * Пуш несёт статус и этап: дочитать задачу нужно, когда сменился статус. Скрытая вкладка больше ничего не ждёт —
     * вернувшись, она перечитает список; видимая дочитывает этап, а ход — только если его кто-то показывает.
     */
    private onJobChanged(event: IBackgroundJobChangedEvent): void {
        const known = this.state.jobs[event.jobId]?.job;
        if (known?.status === event.status) {
            if (document.visibilityState === 'hidden') return;
            const progressOnly = (known.stage ?? null) === (event.stage ?? null);
            if (progressOnly && !this.isProgressShown(event.jobId)) return;
        }
        void this.refreshJob(event.jobId);
    }

    private isProgressShown(jobId: string): boolean {
        return this.progressViewers > 0 || this.jobListeners.has(jobId) || this.observed.has(jobId);
    }

    private keepWatching(jobId: string): () => void {
        const mark = () => {
            if (document.visibilityState === 'hidden') return;
            this.deps.service.watch(jobId).catch(() => {
                /* Пропущенная отметка — в худшем случае лишнее уведомление. */
            });
        };
        mark();
        const timer = setInterval(mark, WATCH_INTERVAL_MS);
        document.addEventListener('visibilitychange', mark);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', mark);
        };
    }

    /** Задачи больше нет: убрать её и сказать подписчикам, даже если в состоянии её и не было. */
    private forgetJob(jobId: string): void {
        const known = jobId in this.state.jobs;
        this.dispatch({ type: 'jobGone', jobId });
        if (!known) this.jobListeners.get(jobId)?.forEach((listener) => listener(null));
    }

    private dispatch(action: BackgroundJobsAction): void {
        const previous = this.state;
        let next = backgroundJobsReducer(previous, action);
        if (action.type === 'listLoaded' || action.type === 'jobLoaded') next = this.applyShownHere(next);
        if (action.type === 'jobLoaded') {
            next = backgroundJobsReducer(next, {
                type: 'pruned',
                now: action.now,
                keep: (jobId) => this.jobListeners.has(jobId) || this.observed.has(jobId) || this.held.has(jobId),
            });
        }
        if (next === previous) return;
        this.state = next;

        const finished = findJustFinished(previous, next);
        this.notifyJobListeners(previous, next);
        this.listeners.forEach((listener) => listener());
        finished.forEach((job) => this.finishedListeners.forEach((listener) => listener(job)));
        this.schedulePoll();
    }

    private applyShownHere(state: BackgroundJobsState): BackgroundJobsState {
        const now = this.deps.now();
        let next = state;
        this.shownHere.forEach((at, jobId) => {
            if (now - at >= ANNOUNCED_MEMORY_MS) this.shownHere.delete(jobId);
            else next = backgroundJobsReducer(next, { type: 'shownInWindow', jobId });
        });
        return next;
    }

    private notifyJobListeners(previous: BackgroundJobsState, next: BackgroundJobsState): void {
        this.jobListeners.forEach((listeners, jobId) => {
            const before = previous.jobs[jobId];
            const after = next.jobs[jobId];
            if (before === after) return;
            // Сброс при выходе — не «задачи нет»: окно закроется вместе с сессией само. Выключенный механизм (404
            // списка) — тоже: подписчик дочитывает свою задачу сам и решает по ответу о ней.
            if (!after && (next === initialBackgroundJobsState || next.enabled === false)) return;
            listeners.forEach((listener) => listener(after?.job ?? null));
        });
    }

    private clearListRetry(): void {
        if (this.listRetryTimer !== null) {
            clearTimeout(this.listRetryTimer);
            this.listRetryTimer = null;
        }
    }

    /** Повтор списка после сбоя — несколько раз с растущей паузой; скрытая вкладка перечитает его, вернувшись. */
    private scheduleListRetry(): void {
        this.clearListRetry();
        const delay = LIST_RETRY_DELAYS_MS[this.listFailures];
        this.listFailures += 1;
        if (delay === undefined) return;
        this.listRetryTimer = setTimeout(() => {
            this.listRetryTimer = null;
            if (document.visibilityState !== 'hidden') this.refreshList();
        }, this.listRetryDelay(delay));
    }

    private listRetryDelay(delay: number): number {
        return Math.round(delay * (1 - LIST_RETRY_JITTER + 2 * LIST_RETRY_JITTER * this.random()));
    }

    private random(): number {
        return (this.deps.random ?? Math.random)();
    }

    private clearReconnect(): void {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * Переподключение после разрыва: события, разосланные без соединения, потеряны — список перечитывается, но после
     * случайной паузы, а скрытая вкладка только помечает его устаревшим и перечитает, вернувшись. Запрос, ушедший за
     * время паузы (возврат на вкладку), перечитывание заменяет.
     */
    private refreshListAfterReconnect(): void {
        this.clearReconnect();
        const reconnectedAt = this.deps.now();
        if (document.visibilityState === 'hidden') {
            this.listStale = true;
            return;
        }
        this.reconnectTimer = setTimeout(
            () => {
                this.reconnectTimer = null;
                if (this.lastListRequestAt > reconnectedAt) return;
                if (document.visibilityState === 'hidden') this.listStale = true;
                else this.refreshList();
            },
            Math.floor(this.random() * RECONNECT_JITTER_MS),
        );
    }

    private clearPoll(): void {
        if (this.pollTimer !== null) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private schedulePoll(): void {
        this.clearPoll();
        if (!this.started || this.state.enabled === false || selectActiveJobs(this.state).length === 0) return;

        const interval = this.deps.hub.isConnected ? PUSH_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
        this.pollTimer = setTimeout(() => {
            this.pollTimer = null;
            // Скрытая вкладка ничего не показывает; вернувшись, она перечитает список целиком.
            if (document.visibilityState === 'hidden') {
                this.schedulePoll();
                return;
            }
            const active = selectActiveJobs(this.state);
            void Promise.all(active.map((job) => this.refreshJob(job.jobId))).finally(() => {
                if (this.pollTimer === null) this.schedulePoll();
            });
        }, interval);
    }
}

/** Ещё одна ссылка на задачу в счётчике; возвращённая функция снимает её (повторный вызов ничего не делает). */
const retain = (counts: Map<string, number>, jobId: string): (() => void) => {
    counts.set(jobId, (counts.get(jobId) ?? 0) + 1);
    let released = false;
    return () => {
        if (released) return;
        released = true;
        const count = (counts.get(jobId) ?? 1) - 1;
        if (count > 0) counts.set(jobId, count);
        else counts.delete(jobId);
    };
};

/** Запомнить момент для `jobId`, заодно забыв устаревшие: память вкладки об итогах нужна недолго. */
const remember = (memory: Map<string, number>, jobId: string, now: number): void => {
    memory.forEach((at, id) => {
        if (now - at >= ANNOUNCED_MEMORY_MS) memory.delete(id);
    });
    memory.set(jobId, now);
};

export const backgroundJobsStore = new BackgroundJobsStore({
    service: BackgroundJobsService,
    hub: notificationHub,
    now: () => Date.now(),
});
